import { Box, CircularProgress, Divider, Link, Popover, useTheme } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useEffect, useState } from "react";

import dayjs from "../../utils/dayjs";
import { usePerspective } from "../../contexts/app/provider";
import { GetTransformedRawParquetDocument } from "../../services/perspective_api/__generated__/perspective_server-types";
import { parseParquetData, FullParsedParquetData } from "../../utils/parquet";
import { RunSettings } from "../../services/appsync/types";
import {
  closePoCellPopover,
  usePoCellPopoverTarget,
  PoCellPopoverTarget,
} from "./store";

interface PoCellDetailRow {
  purchase_order_id: string;
  sku: string;
  sku_name: string | null;
  location: string;
  supplier: string | null;
  quantity: number;
  unit_cost: number | null;
  order_placed_date: number | null;
  order_delivery_date: number | null;
  status: string;
  lead_time_days: number | null;
  /** Uncapped PO count for the cell (rows are capped at 10 server-side). */
  total_po_count: number | null;
}

interface FetchEntry {
  loading: boolean;
  error: string | null;
  rows: PoCellDetailRow[] | null;
}

// Module-level cache so reopening a cell never refetches within a session.
// Keyed by run + cell identity; a new run settings object is a new key.
const detailCache = new Map<string, FetchEntry>();

const toNumberOrNull = (v: unknown): number | null => {
  if (v == null) return null;
  const n = Number(v); // arrow Int64 arrives as bigint
  return Number.isNaN(n) ? null : n;
};

function arrowToRows(parsed: FullParsedParquetData): PoCellDetailRow[] {
  return parsed.arrowTable.toArray().map((r: any) => ({
    purchase_order_id: String(r.purchase_order_id ?? ""),
    sku: String(r.sku ?? ""),
    sku_name: r.sku_name != null ? String(r.sku_name) : null,
    location: String(r.location ?? ""),
    supplier: r.supplier != null ? String(r.supplier) : null,
    quantity: toNumberOrNull(r.quantity) ?? 0,
    unit_cost: toNumberOrNull(r.unit_cost),
    order_placed_date: toNumberOrNull(r.order_placed_date),
    order_delivery_date: toNumberOrNull(r.order_delivery_date),
    status: String(r.status ?? ""),
    lead_time_days: toNumberOrNull(r.lead_time_days),
    total_po_count: toNumberOrNull(r.total_po_count),
  }));
}

const formatDate = (ms: number | null): string =>
  ms == null ? "—" : dayjs.utc(ms).format("MM/DD/YY");

const formatQty = (n: number): string =>
  n.toLocaleString(undefined, { maximumFractionDigits: 0 });

const FIELD_LABEL_SX = {
  fontSize: "10.5px",
  fontWeight: 600,
  letterSpacing: "0.04em",
  textTransform: "uppercase" as const,
  color: (t: any) => t.palette.atomic.muted,
};

const FIELD_VALUE_SX = {
  fontSize: "12px",
  fontVariantNumeric: "tabular-nums",
  color: (t: any) => t.palette.atomic.ink,
};

function FieldRow({ label, value }: { label: string; value: string }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        gap: "16px",
        py: "3px",
      }}
    >
      <Box sx={FIELD_LABEL_SX}>{label}</Box>
      <Box sx={FIELD_VALUE_SX}>{value}</Box>
    </Box>
  );
}

/**
 * Popover with the individual POs behind one open/simulated breakdown cell.
 *
 * Render-isolation contract: this component (and its module store) is the ONLY
 * thing that re-renders on open/close/data-arrival. It must never feed state
 * back into the matrix table — the clicked-cell outline is applied
 * imperatively to the anchor element for the same reason.
 */
export function PoCellPopover({
  runSettings,
  dateHierarchy,
}: {
  runSettings: RunSettings | null;
  dateHierarchy: "day" | "week" | "month";
}) {
  const target = usePoCellPopoverTarget();
  const { perspectiveApiService } = usePerspective();
  const theme = useTheme();
  const outlineColor = theme.palette.atomic.brandBlue;
  const [entry, setEntry] = useState<FetchEntry>({
    loading: false,
    error: null,
    rows: null,
  });

  // Outline the clicked cell while the popover is open (imperative on purpose
  // — see render-isolation contract above).
  useEffect(() => {
    if (!target) return;
    const el = target.anchorEl;
    const prevOutline = el.style.outline;
    const prevOffset = el.style.outlineOffset;
    el.style.outline = `2px solid ${outlineColor}`;
    el.style.outlineOffset = "-2px";
    return () => {
      el.style.outline = prevOutline;
      el.style.outlineOffset = prevOffset;
    };
  }, [target, outlineColor]);

  useEffect(() => {
    if (!target) return;
    const cacheKey = JSON.stringify([
      runSettings,
      target.sku,
      target.location,
      target.periodStartMs,
      target.kind,
      target.bucketBy,
      dateHierarchy,
    ]);
    // Only completed fetches count as cache hits. A stale {loading:true}
    // entry (popover closed mid-flight — the cleanup unsubscribes) or an
    // error entry must refetch, or reopening would spin forever.
    const cached = detailCache.get(cacheKey);
    if (cached?.rows) {
      setEntry(cached);
      return;
    }
    const loadingEntry: FetchEntry = { loading: true, error: null, rows: null };
    detailCache.set(cacheKey, loadingEntry);
    setEntry(loadingEntry);

    const client = perspectiveApiService.getClient();
    const sub = client
      .subscribe({
        query: GetTransformedRawParquetDocument,
        variables: {
          datasetName: "po_plan_edit",
          transformId: "po_cell_details",
          folder: "outputs",
          runSettingsJson: runSettings ?? null,
          comparedToRunSettingsJson: null,
          format: "parquet" as const,
          params: {
            sku: target.sku,
            // Omit location for SKU-level cells so the transform matches on
            // sku alone (across all locations).
            ...(target.location != null
              ? { location: target.location }
              : {}),
            period_start: new Date(target.periodStartMs).toISOString(),
            date_hierarchy: dateHierarchy,
            kind: target.kind,
            bucket_by: target.bucketBy,
          },
        },
      })
      .subscribe({
        next: async (res: any) => {
          const parquetData = res?.data?.getTransformedRawParquet?.parquetData;
          if (!parquetData || typeof parquetData !== "string") return;
          try {
            const parsed = await parseParquetData(
              parquetData,
              !parquetData.startsWith("http")
            );
            const rows = arrowToRows(parsed as FullParsedParquetData);
            const done: FetchEntry = { loading: false, error: null, rows };
            detailCache.set(cacheKey, done);
            setEntry(done);
          } catch (e: any) {
            const failed: FetchEntry = {
              loading: false,
              error: String(e?.message || e),
              rows: null,
            };
            detailCache.set(cacheKey, failed);
            setEntry(failed);
          }
        },
        error: (err: any) => {
          const failed: FetchEntry = {
            loading: false,
            error: String(err?.message || err),
            rows: null,
          };
          detailCache.set(cacheKey, failed);
          setEntry(failed);
        },
      });
    return () => sub.unsubscribe();
  }, [target, runSettings, dateHierarchy, perspectiveApiService]);

  if (!target) return null;

  return (
    <Popover
      open
      anchorEl={target.anchorEl}
      onClose={closePoCellPopover}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      transformOrigin={{ vertical: "top", horizontal: "center" }}
      slotProps={{
        paper: {
          sx: {
            padding: "12px 14px",
            minWidth: 240,
            maxWidth: 320,
            border: "1px solid",
            borderColor: (t) => t.palette.atomic.border,
            borderRadius: "8px",
          },
        },
      }}
    >
      <PoCellPopoverContent target={target} entry={entry} />
    </Popover>
  );
}

function HeaderChip({ kind }: { kind: PoCellPopoverTarget["kind"] }) {
  const label =
    kind === "open"
      ? "OPEN PURCHASE ORDER"
      : kind === "simulated"
        ? "SIMULATED PO"
        : "PURCHASE ORDERS";
  return (
    <Box
      sx={{
        display: "inline-block",
        fontSize: "10px",
        fontWeight: 700,
        letterSpacing: "0.06em",
        borderRadius: "4px",
        padding: "2px 7px",
        mb: "8px",
        backgroundColor: (t) =>
          kind === "simulated" ? t.palette.atomic.orange1 : t.palette.atomic.blue1,
        color: (t) =>
          kind === "simulated" ? t.palette.atomic.ink : t.palette.atomic.brandBlueDark,
      }}
    >
      {label}
    </Box>
  );
}

function PoCellPopoverContent({
  target,
  entry,
}: {
  target: PoCellPopoverTarget;
  entry: FetchEntry;
}) {
  if (entry.loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: "12px" }}>
        <CircularProgress size={18} />
      </Box>
    );
  }
  if (entry.error) {
    return (
      <Box sx={{ fontSize: "12px", color: (t) => t.palette.atomic.red1 }}>
        Couldn't load PO details: {entry.error}
      </Box>
    );
  }
  const rows = entry.rows ?? [];
  if (rows.length === 0) {
    return (
      <Box>
        <HeaderChip kind={target.kind} />
        <Box sx={{ fontSize: "12px", color: (t) => t.palette.atomic.muted }}>
          No PO details available for this cell.
        </Box>
      </Box>
    );
  }

  const totalCount = rows[0]?.total_po_count ?? rows.length;

  return (
    <Box>
      <HeaderChip kind={target.kind} />
      {totalCount > rows.length && (
        <Box
          sx={{
            fontSize: "11px",
            fontWeight: 600,
            color: (t) => t.palette.atomic.muted,
            mb: "6px",
          }}
        >
          Showing top {rows.length} of {formatQty(totalCount)} POs by quantity
        </Box>
      )}
      <Box sx={{ maxHeight: 320, overflowY: "auto", pr: "2px" }}>
      {rows.map((row, i) => {
        // For aggregate ("all") cells each row picks its own layout from its
        // status: ERP rows use the open layout, engine rows the simulated one.
        const rowIsOpen =
          target.kind === "all"
            ? row.status !== "Needs Review"
            : target.kind === "open";
        return (
        <Box key={`${row.purchase_order_id}-${i}`}>
          {i > 0 && <Divider sx={{ my: "8px" }} />}
          {rowIsOpen ? (
            <>
              <Box
                sx={{
                  fontSize: "16px",
                  fontWeight: 700,
                  fontVariantNumeric: "tabular-nums",
                  color: (t) => t.palette.atomic.ink,
                  mb: "6px",
                }}
              >
                {row.purchase_order_id}
              </Box>
              <FieldRow label="SKU" value={row.sku} />
              <FieldRow label="Location" value={row.location} />
              <FieldRow label="Quantity" value={formatQty(row.quantity)} />
              <FieldRow
                label="Arrival date"
                value={formatDate(row.order_delivery_date)}
              />
              <FieldRow label="Status" value={row.status} />
            </>
          ) : (
            <>
              <Box
                sx={{
                  backgroundColor: (t) => t.palette.atomic.orange1,
                  borderRadius: "6px",
                  padding: "8px 10px",
                  mb: "6px",
                }}
              >
                <Box sx={FIELD_LABEL_SX}>Recommended arrival</Box>
                <Box
                  sx={{
                    fontSize: "16px",
                    fontWeight: 700,
                    fontVariantNumeric: "tabular-nums",
                    color: (t) => t.palette.atomic.ink,
                  }}
                >
                  {formatDate(row.order_delivery_date)}
                </Box>
              </Box>
              <FieldRow label="SKU" value={row.sku} />
              <FieldRow label="Location" value={row.location} />
              <FieldRow
                label="Suggested qty"
                value={formatQty(row.quantity)}
              />
              <FieldRow
                label="Place by"
                value={formatDate(row.order_placed_date)}
              />
              <FieldRow
                label="Lead time"
                value={
                  row.lead_time_days != null
                    ? `${row.lead_time_days} days`
                    : "—"
                }
              />
            </>
          )}
        </Box>
        );
      })}
      </Box>
      <Box sx={{ mt: "10px" }}>
        <Link
          component={RouterLink}
          to={`/po-inbox?filters=${encodeURIComponent(
            JSON.stringify([
              // Preserve the default "Needs Review" tab (a URL `filters` param
              // replaces the view's default filter list, so we re-include the
              // status filter that drives the tabs) and add a SKU filter so the
              // inbox lands pre-scoped to the PO cell the user clicked.
              ["status", "in", ["Needs Review"]],
              ["sku", "in", [target.sku]],
            ]),
          )}`}
          onClick={closePoCellPopover}
          sx={{
            fontSize: "12px",
            fontWeight: 600,
            color: (t) => t.palette.atomic.brandBlueDark,
          }}
        >
          View in PO Inbox
        </Link>
      </Box>
    </Box>
  );
}
